import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-config';
import { WorkflowTaskDecision, WorkflowTaskDto, WorkflowTaskPageDto } from '../models/workflow-task.models';

/** The "My approvals" endpoints. `decide` keeps the HTTP response because the status code carries the outcome
 * (200 done, 202 another step must still approve); every other outcome arrives as an HttpErrorResponse. */
@Injectable({ providedIn: 'root' })
export class WorkflowTasksService {
  private readonly http = inject(HttpClient);

  list(actionKey: string | null, page: number, pageSize: number): Observable<WorkflowTaskPageDto> {
    let params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    if (actionKey) params = params.set('actionKey', actionKey);
    return this.http.get<WorkflowTaskPageDto>(`${API_BASE_URL}/workflow-tasks`, { params });
  }

  count(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${API_BASE_URL}/workflow-tasks/count`);
  }

  get(id: string): Observable<WorkflowTaskDto> {
    return this.http.get<WorkflowTaskDto>(`${API_BASE_URL}/workflow-tasks/${id}`);
  }

  /** Calls the endpoint the task itself names (its own approve/reject), with the optional note as the body. */
  decide(task: WorkflowTaskDto, decision: WorkflowTaskDecision, note: string | null): Observable<HttpResponse<unknown>> {
    const endpoint = decision === 'approve' ? task.approve : task.reject;
    if (!endpoint) throw new Error(`Task ${task.id} has no ${decision} endpoint.`);
    return this.http.request<unknown>(endpoint.method, `${API_BASE_URL}/${endpoint.path.replace(/^\/+/, '')}`, {
      body: { note },
      observe: 'response',
    });
  }
}

/** The navigation badge: how many approvals wait for the signed-in user. Refreshed by whoever calls `refresh()`;
 * `start()` (idempotent) also polls every 60 s while the tab is visible. */
@Injectable({ providedIn: 'root' })
export class WorkflowTasksBadgeService {
  private readonly tasks = inject(WorkflowTasksService);
  private timer: ReturnType<typeof setInterval> | null = null;

  readonly count = signal(0);

  refresh(): void {
    this.tasks.count().subscribe({
      next: (r) => this.count.set(r.count),
      error: () => {
        // A failed badge poll must never disturb the page; keep the last known count.
      },
    });
  }

  start(): void {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') this.refresh();
    }, 60_000);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.count.set(0);
  }
}
